// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import type { TeacherArtifact } from '@/types';

function parseJsonCandidate(value: unknown): unknown {
  if (typeof value !== 'string') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function parseJsonLikeContent(value: string): unknown {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const direct = parseJsonCandidate(trimmed);
  if (direct && typeof direct === 'object') {
    return direct;
  }
  const fenced = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  if (fenced) {
    return parseJsonCandidate(fenced[1]);
  }
  const objectMatch = trimmed.match(/\{[\s\S]*\}/);
  if (objectMatch) {
    return parseJsonCandidate(objectMatch[0]);
  }
  return null;
}

function hasDiscussionShape(value: unknown): value is {
  main_question: string;
  sub_questions: Array<Record<string, unknown>>;
} {
  return Boolean(
    value &&
      typeof value === 'object' &&
      typeof (value as Record<string, unknown>).main_question === 'string' &&
      Array.isArray((value as Record<string, unknown>).sub_questions)
  );
}

function renderDiscussionMarkdown(value: {
  main_question: string;
  sub_questions: Array<Record<string, unknown>>;
}): string {
  const lines = ['## 讨论设计产物', '', '### 主问题', value.main_question, ''];
  value.sub_questions.slice(0, 3).forEach((subQuestion, index) => {
    lines.push(`### 子问题 ${index + 1}`);
    lines.push(String(subQuestion.question || ''));
    lines.push('');
    lines.push('建议学生回答：');
    (Array.isArray(subQuestion.expected_answers) ? subQuestion.expected_answers : [])
      .slice(0, 3)
      .forEach((item) => lines.push(`- ${String(item || '')}`));
    lines.push('');
    lines.push('常见误区：');
    (Array.isArray(subQuestion.misconceptions) ? subQuestion.misconceptions : [])
      .slice(0, 3)
      .forEach((item) => lines.push(`- ${String(item || '')}`));
    lines.push('');
    lines.push(`教师追问建议：${String(subQuestion.teacher_follow_up || '')}`);
    lines.push('');
  });
  return lines.join('\n').trim();
}

export function toTeacherArtifact(value: unknown): TeacherArtifact | null {
  const candidate = parseJsonCandidate(value);
  if (!candidate || typeof candidate !== 'object') {
    return null;
  }
  const record = candidate as Record<string, unknown>;
  if (
    typeof record.artifact_id !== 'string' ||
    typeof record.artifact_type !== 'string' ||
    typeof record.rendered_markdown !== 'string' ||
    typeof record.raw_text !== 'string'
  ) {
    return null;
  }
  return record as unknown as TeacherArtifact;
}

export function toTeacherArtifactFromArtifacts(
  value: unknown,
  artifactType?: string
): TeacherArtifact | null {
  if (!Array.isArray(value)) {
    return null;
  }
  for (const item of value) {
    const artifact = toTeacherArtifact(item);
    if (!artifact) {
      continue;
    }
    if (artifactType && artifact.artifact_type !== artifactType) {
      continue;
    }
    return artifact;
  }
  return null;
}

export function resolveTeacherArtifact(
  value: unknown,
  artifactType?: string
): TeacherArtifact | null {
  const candidate = parseJsonCandidate(value);
  const direct = toTeacherArtifact(candidate);
  if (direct && (!artifactType || direct.artifact_type === artifactType)) {
    return direct;
  }
  if (!candidate || typeof candidate !== 'object') {
    return null;
  }
  const record = candidate as Record<string, unknown>;
  return (
    toTeacherArtifact(record.artifact) ??
    toTeacherArtifact(record.teacher_artifact) ??
    toTeacherArtifactFromArtifacts(record.artifacts, artifactType) ??
    toTeacherArtifactFromArtifacts(record.artifacts)
  );
}

export function teacherArtifactMarkdown(value: unknown): string {
  const artifact = toTeacherArtifact(value);
  if (!artifact) {
    return '';
  }
  return artifact.rendered_markdown || artifact.raw_text || '';
}

export function teacherArtifactMarkdownFromArtifacts(value: unknown): string {
  if (!Array.isArray(value)) {
    return '';
  }
  for (const item of value) {
    const markdown = teacherArtifactMarkdown(item);
    if (markdown) {
      return markdown;
    }
  }
  return '';
}

export function teacherArtifactMarkdownFromStructuredContent(value: unknown): string {
  const artifactMarkdown =
    teacherArtifactMarkdown(value) ||
    teacherArtifactMarkdownFromArtifacts(
      value && typeof value === 'object' ? (value as Record<string, unknown>).artifacts : null
    );
  if (artifactMarkdown) {
    return artifactMarkdown;
  }

  const parsed =
    typeof value === 'string'
      ? parseJsonLikeContent(value)
      : value && typeof value === 'object'
        ? value
        : null;
  if (!parsed || typeof parsed !== 'object') {
    return '';
  }

  const record = parsed as Record<string, unknown>;
  if (typeof record.predictions_markdown === 'string' && record.predictions_markdown.trim()) {
    return record.predictions_markdown.trim();
  }
  if (hasDiscussionShape(record)) {
    return renderDiscussionMarkdown(record);
  }
  return '';
}

function deepMessageToolCalls(value: unknown): Array<Record<string, unknown>> {
  if (!value || typeof value !== 'object') return [];
  const record = value as Record<string, unknown>;
  const toolCalls = record.toolCalls ?? record.tool_calls;
  return Array.isArray(toolCalls)
    ? toolCalls.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
    : [];
}

function toolCallArguments(toolCall: Record<string, unknown>): unknown {
  const fn = toolCall.function;
  if (fn && typeof fn === 'object' && !Array.isArray(fn)) {
    const args = (fn as Record<string, unknown>).arguments;
    if (args != null) return args;
  }
  return toolCall.args;
}

export function extractLatestTeacherArtifactFromMessages(
  messages: Array<Record<string, unknown>> | null | undefined
): TeacherArtifact | null {
  if (!Array.isArray(messages) || messages.length === 0) return null;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    const direct =
      resolveTeacherArtifact(message?.content) ??
      resolveTeacherArtifact(message?.artifact) ??
      resolveTeacherArtifact(message?.teacher_artifact) ??
      resolveTeacherArtifact(message?.artifacts);
    if (direct) {
      return direct;
    }

    const toolCalls = deepMessageToolCalls(message);
    for (let toolIndex = toolCalls.length - 1; toolIndex >= 0; toolIndex -= 1) {
      const artifact = resolveTeacherArtifact(toolCallArguments(toolCalls[toolIndex]));
      if (artifact) {
        return artifact;
      }
    }
  }
  return null;
}
